import { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Table } from '../../../iba-components/Table'
import { Input } from '../../../iba-components/Input'
import { Select } from '../../../iba-components/Select'
import { getColumnList } from './tableColumns'
import { getAnalysisList } from '../../api/analysis'
import { getSearchTableSelect, DEFAULT_SORT } from './constants' // constants をインポート
import './consultingAnalysisList.css'
import { Loading } from '../../components/Loading'
import { Modal } from '../../../iba-components/Modal'
import { Button } from '../../../iba-components/Button'
import { showPopBanner } from '../../../iba-components/PopBanner'

import styles from './index.module.css'

const ConsultingAnalysisList = () => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [list, setList] = useState([])
    const [displayDataList, setDisplayDataList] = useState([])
    const [statusCount, setStatusCount] = useState({})

    // 検索機能用のstate
    const [selectedValue, setSelectedValue] = useState(getSearchTableSelect(t)[0].value)
    const [selectedLabel, setSelectedLabel] = useState(getSearchTableSelect(t)[0].content)
    const [userInput, setUserInput] = useState('')

    const [visibleModal, setVisibleModal] = useState(false)
    const [modalParams, setModalParams] = useState(null)

    // 検索機能用のハンドラー
    const handleChangeSelect = (value, content) => {
        if (loading) return
        setSelectedValue(value)
        setSelectedLabel(content)
    }

    const handleChangeInput = (inputValue) => {
        if (loading) return
        setUserInput(inputValue)
    }

    // フィルタリング関数
    const filterDataList = () => {
        if (!userInput) return list
        const userInputOfLowerCase = userInput.toLowerCase()
        let filterDataList = []

        if (selectedValue === 'All') {
            filterDataList = list?.filter(({ usage_or_client_folder, theme_name, status, user_name }) => {
                // statusの場合は数値を文字列に変換して検索
                const statusText = getStatusText(status).toLowerCase()

                return (
                    (usage_or_client_folder || '').toLowerCase().includes(userInputOfLowerCase) ||
                    (theme_name || '').toLowerCase().includes(userInputOfLowerCase) ||
                    statusText.includes(userInputOfLowerCase) ||
                    (user_name || '').toLowerCase().includes(userInputOfLowerCase)
                )
            })
        } else if (selectedValue === 'status') {
            // ステータスの場合は特別処理
            filterDataList = list?.filter(({ status }) => {
                const statusText = getStatusText(status).toLowerCase()
                return statusText.includes(userInputOfLowerCase)
            })
        } else {
            filterDataList = list?.filter((item) => {
                const value = item[selectedValue] || ''
                return value.toString().toLowerCase().includes(userInputOfLowerCase)
            })
        }

        return filterDataList
    }

    // ステータステキストを取得する関数
    const getStatusText = (status) => {
        return status === 0
            ? t('consultingAnalysis.list.status.pending')
            : status === 1
            ? t('consultingAnalysis.list.status.analysis')
            : status === 2
            ? t('consultingAnalysis.list.status.success')
            : status === 3
            ? t('consultingAnalysis.list.status.failed')
            : t('consultingAnalysis.list.status.undefined')
    }

    // 検索フォーム送信ハンドラー
    const handleSubmitForm = (e) => {
        e.preventDefault()
        setDisplayDataList(filterDataList())
        document.activeElement.blur()
    }

    // fetch data from DB
    const fetchData = async () => {
        setLoading(true)
        const { data, statusCount } = await getAnalysisList()
        setList(data)
        setStatusCount(statusCount || {})
        setLoading(false)
    }

    useEffect(() => {
        fetchData()
    }, [])

    // データが変更されたときに表示用データも更新
    useEffect(() => {
        setDisplayDataList(filterDataList())
    }, [list])

    const handleClickDownload = (id) => {
        const url = `/api/analysis/downloadAnalysis?id=${id}`
        window.open(url, '_blank')
    }

    const handleClickDatabricksLink = (url) => {
        window.open(url, '_blank')
    }

    const handleClickParameters = (row) => {
        setModalParams({
            parameters_str: row.parameters_str,
            usage_or_client_folder: row.usage_or_client_folder,
            theme_name: row.theme_name,
            patent_count: row.patent_count,
            patent_period_start: row.patent_period_start,
            patent_period_end: row.patent_period_end,
            investment_period_start: row.investment_period_start,
            investment_period_end: row.investment_period_end,
            ipc_digit: row.ipc_digit,
            one_cluster_one_ipc: row.one_cluster_one_ipc,
            is_cvc: row.is_cvc
        })
        setVisibleModal(true)
    }

    const analysisDetailsModal = () => {
        if (!modalParams) return null

        const paramItems = [
            { label: t('consultingAnalysis.list.usage_or_client_folder'), value: modalParams.usage_or_client_folder },
            { label: t('consultingAnalysis.list.theme_name'), value: modalParams.theme_name },
            { label: t('consultingAnalysis.list.patent_count'), value: modalParams.patent_count },
            { label: t('consultingAnalysis.list.patent_period_start'), value: modalParams.patent_period_start },
            { label: t('consultingAnalysis.list.patent_period_end'), value: modalParams.patent_period_end },
            { label: t('consultingAnalysis.list.investment_period_start'), value: modalParams.investment_period_start },
            { label: t('consultingAnalysis.list.investment_period_end'), value: modalParams.investment_period_end },
            { label: t('consultingAnalysis.list.ipc_digit'), value: modalParams.ipc_digit },
            { label: t('consultingAnalysis.list.one_cluster_one_ipc'), value: modalParams.one_cluster_one_ipc },
            { label: t('consultingAnalysis.list.is_cvc'), value: modalParams.is_cvc }
        ]

        const handleCopyParameters = async () => {
            try {
                await navigator.clipboard.writeText(modalParams.parameters_str)
                showPopBanner({
                    message: t('consultingAnalysis.list.copySuccess'),
                    status: 'success',
                    duration: 3000
                })
            } catch (err) {
                showPopBanner({
                    message: t('consultingAnalysis.list.copyFailed'),
                    status: 'error',
                    duration: 3000
                })
            }
        }

        return (
            <Modal
                wrapperClassName="consulting-analysis-modal"
                placement="top"
                visible={visibleModal}
                width={800}
                onCancel={() => setVisibleModal(false)}
            >
                <div className="confirm-modal-content">
                    <h3>{t('consultingAnalysis.list.detailTitle')}</h3>
                    <div className="confirm-params-list">
                        {paramItems.map((item, index) => (
                            <div key={index} className="confirm-param-item">
                                <span className="confirm-param-label">{item.label}:</span>
                                <span className="confirm-param-value">{item.value || '-'}</span>
                            </div>
                        ))}
                    </div>
                    <div className="confirm-modal-buttons">
                        <Button label={t('consultingAnalysis.list.copyParameters')} onClick={handleCopyParameters} />
                    </div>
                </div>
            </Modal>
        )
    }

    return (
        <div className="common-page consulting-analysis-container">
            <div className="consulting-analysis-banner">
                <h4></h4>
                <div className="d-flex justify-content-between align-items-end">
                    <div className="sub-title">{t('consultingAnalysis.list.title')}</div>
                    <div className={styles.statusSummary}>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>{t('consultingAnalysis.list.status.pending')}:</span>
                            <span className={styles.statusCount}>{statusCount[0] || 0}</span>
                        </div>
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>{t('consultingAnalysis.list.status.analysis')}:</span>
                            <span className={styles.statusCount}>{statusCount[1] || 0}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.table_area}>
                <Loading visible={loading}>
                    <div>
                        <Table
                            data={displayDataList}
                            noDataText={loading ? '' : t('common.noData')}
                            columns={getColumnList(
                                t,
                                handleClickDatabricksLink,
                                handleClickParameters,
                                handleClickDownload
                            )}
                            sort={DEFAULT_SORT}
                            isBottomPagination={false}
                            isDownload={false}
                        >
                            {/* 検索フォームを追加 */}
                            <form className={styles.search_wrap} onSubmit={handleSubmitForm}>
                                <Select
                                    dataList={getSearchTableSelect(t)}
                                    itemKey="value"
                                    inputProps={{ value: selectedLabel }}
                                    handleChangeSelect={handleChangeSelect}
                                    selectedValue={selectedValue}
                                />
                                <Input
                                    className={`${styles.user_input}`}
                                    placeholder={t('consultingAnalysis.list.userInputPlaceholder')} // 翻訳キーは適宜追加してください
                                    search
                                    value={userInput}
                                    onChange={handleChangeInput}
                                />
                            </form>
                        </Table>
                    </div>
                </Loading>
            </div>
            {visibleModal && analysisDetailsModal()}
        </div>
    )
}

export default withRouter(ConsultingAnalysisList)
